import React from 'react';
import { connect, ErrorMessage, FormikContextType } from 'formik';
import styled, { useTheme } from 'styled-components';

import DatePickerField from '@shared/fields/DatePickerField';
import { Switcher } from '@shared/fields';
import { LabeledField } from '../../LabeledField';
import { StarRating } from '../../StarRating';
import Textareafield from '../../fields/Textareafield';
import { TextareaInfo } from '../TextareaInfo';
import { Checkbox } from '../../fields/Checkbox';
import { CheckboxLabel } from '../CheckboxLabel';
import Btn from '../../buttons/Btn';
import { isMobile, REVIEW_MIN_DATE_ISO } from '../../../constants';
import { FORM_TEXTS } from './formDictionary';
import { BLUE_GREY, FONT_FAMILY_MAIN } from '../../../theme';
import { withScrollToError } from '../../../hocs/withScrollToError';
import { MESSAGE_MIN_LENGTH } from './constants';
import stores from '../../../stores';
import media from '../../../utils/mediaQueries';
import { ReviewFormValues } from './types';
import { ErrorMessageContainer } from '../../fields/common/ErrorMessageContainer';

const FieldComment = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.1px;
  color: ${BLUE_GREY};
  margin-top: 8px;
`;

const CheckboxRow = styled.div`
  margin: 51px 0 19px;  
`;

const DrivingSchoolTypeWrap = styled.div`
  input:checked + label {
    ${({ theme }) => `
      background-color: ${theme.drivingSchoolTypeColor} !important;
      color: ${theme.drivingSchoolSelectedTypeFontColor} !important;
    `}
    
  }
  
  ${media.mobile`
    label {
      font-size: 14px;
      padding: 0 8px;
    }
  `}
`;

type FormProps = {
  reviewTextRef: React.RefObject<HTMLTextAreaElement>,
};

type FormikProps = {
  formik: FormikContextType<ReviewFormValues>;
};

const Form = (props: FormProps & FormikProps) => {
  const {
    starsData, textarea, registrationData, waitingRoomData, examRoomData, equipmentData, resultAvailabilityData, examDate, drivingSchoolType,
  } = FORM_TEXTS;

  const { formik } = props;
  const {
    values, errors, touched, handleChange, handleBlur, isSubmitting, handleSubmit, setFieldValue,
  } = formik;
  const { reviewTextRef } = props;

  const theme = useTheme();

  return (
    <form onSubmit={handleSubmit}>
      <LabeledField isRequired label={starsData.label} error={errors.stars && touched.stars}>
        <StarRating
          selectedStars={values.stars}
          onStarClick={(e) => {
            stores.examCenterReviewStore.setRating(e.target.value);
            handleChange(e);
          }}
          name="stars"
        />
        <ErrorMessage component={ErrorMessageContainer} name="stars" />
      </LabeledField>
      <LabeledField label={registrationData.label}>
        <Switcher
          items={registrationData.items}
          name="registrationRate"
          activeItem={values.registrationRate}
          onChange={handleChange}
        />
        <FieldComment>{registrationData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={waitingRoomData.label}>
        <Switcher
          items={waitingRoomData.items}
          name="waitingRoomRate"
          activeItem={values.waitingRoomRate}
          onChange={handleChange}
        />
        <FieldComment>{waitingRoomData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={examRoomData.label}>
        <Switcher
          items={examRoomData.items}
          name="examRoomRate"
          activeItem={values.examRoomRate}
          onChange={handleChange}
        />
        <FieldComment>{examRoomData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={equipmentData.label}>
        <Switcher
          items={equipmentData.items}
          name="equipmentRate"
          activeItem={values.equipmentRate}
          onChange={handleChange}
        />
        <FieldComment>{equipmentData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={resultAvailabilityData.label}>
        <Switcher
          items={resultAvailabilityData.items}
          name="resultAvailabilityRate"
          activeItem={values.resultAvailabilityRate}
          onChange={handleChange}
        />
        <FieldComment>{resultAvailabilityData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={examDate.label}>
        <DatePickerField
          name="examDate"
          placeholderText={examDate.placeholder}
          value={values.examDate}
          maxDate={new Date()} // today
          minDate={new Date(REVIEW_MIN_DATE_ISO)}
          onChange={setFieldValue}
          onBlur={handleBlur}
          dateFormat="dd MMMM yyyy"
          valid={touched.examDate && !errors.examDate}
        />
        <ErrorMessage component={ErrorMessageContainer} name="examDate" />
      </LabeledField>
      <LabeledField
        label={textarea.label}
        isRequired
        isFullWidthLabel
        error={errors.textareaField && touched.textareaField}
      >
        <Textareafield
          name="textareaField"
          minLength={MESSAGE_MIN_LENGTH}
          value={values.textareaField}
          onChange={handleChange}
          onBlur={handleBlur}
          supplementaryInfo={<TextareaInfo tooltipContent="Parlez de l'accueil que vous avez reçu, des conditions d'examen, du matériel, des locaux, etc" />}
          valid={touched.textareaField && !errors.textareaField}
          textareaRef={reviewTextRef}
        />
        <ErrorMessage component={ErrorMessageContainer} name="textareaField" />
        <FieldComment>
          <a href="/a-propos/conditions-particulieres-avis" target="_blank" rel="noopener noreferrer">
            {textarea.rulesLink}
          </a>
          <div>{textarea.comment}</div>
        </FieldComment>
      </LabeledField>
      <LabeledField label={drivingSchoolType.label} isFullWidthLabel>
        <DrivingSchoolTypeWrap>
          <Switcher
            items={drivingSchoolType.items}
            name="drivingSchoolType"
            activeItem={values.drivingSchoolType}
            onChange={(e) => {
              stores.examCenterReviewStore.setSchoolType(e.target.value);
              handleChange(e);
            }}
          />
        </DrivingSchoolTypeWrap>
      </LabeledField>
      <CheckboxRow>
        <Checkbox
          name="isAgreed"
          label={<CheckboxLabel />}
          isChecked={values.isAgreed}
          onChange={handleChange}
          error={errors.isAgreed && touched.isAgreed}
        />
        <ErrorMessage component={ErrorMessageContainer} name="isAgreed" />
      </CheckboxRow>
      <Btn
        type="submit"
        text="Publier l'avis"
        isLoading={isSubmitting}
        disabled={isSubmitting}
        block={isMobile}
        color={theme.buttonColor}
        textColor={theme.buttonTextColor}
      />
    </form>
  );
};

const FormWithScrollToErrors = withScrollToError(connect(Form));

export { FormWithScrollToErrors };
